"use client";

import { useImperativeHandle, useRef, useState, type Ref } from "react";
import { AgreementConfirmation } from "@/features/service-information/AgreementConfirmation";
import { DocumentBody } from "@/features/service-information/DocumentBody";
import { ScrollConsent, type PrivacyConsentHandle } from "@/features/service-information/PrivacyConsent";
import { acceptedSuppliedChoices, suppliedConsentKeys, suppliedDocumentsForRole, suppliedDocumentSequence, type DocumentRole, type SuppliedConsentChoices } from "../supplied-documents";
import type { PreparedOnboardingDocument } from "../personalized-document";
import { updateOrderedConfirmation } from "../confirmation-sequence";
import { SensitiveConsentChoice, type SensitiveConsentSelections } from "./SensitiveConsentChoice";
export { suppliedDocumentsForRole } from "../supplied-documents";

export function SuppliedOnboardingDocuments({ role, signed, choices, onSign, disabled = false, sensitiveOnly = false, hideSensitiveRow = false, prepareDocument, ref }: {
  role: DocumentRole;
  signed: Record<string, boolean>;
  choices?: SuppliedConsentChoices;
  onSign: (id: string, choices?: SuppliedConsentChoices, renderedDigest?: string) => Promise<void>;
  prepareDocument?: (id: string) => Promise<PreparedOnboardingDocument>;
  disabled?: boolean;
  sensitiveOnly?: boolean;
  hideSensitiveRow?: boolean;
  ref?: Ref<PrivacyConsentHandle>;
}) {
  const [selections, setSelections] = useState<SensitiveConsentSelections>(() => ({
    identity: choices?.identity ? true : undefined,
    face: choices?.face ? true : undefined,
    materials: choices?.materials ? true : undefined,
    financial: choices?.financial ? true : undefined,
  }));
  const sensitiveChoices: SuppliedConsentChoices = {
    identity: selections.identity === true, face: selections.face === true,
    materials: selections.materials === true, financial: selections.financial === true,
  };
  function selectChoice(index: number, value: boolean) {
    if (disabled || signed.sensitive) return;
    setSelections(current => updateOrderedConfirmation(suppliedConsentKeys, current, suppliedConsentKeys[index], value, undefined, (_key, selected) => selected === true));
  }
  const readers = useRef<Record<string, PrivacyConsentHandle | null>>({});
  const [prepared, setPrepared] = useState<Record<string, PreparedOnboardingDocument>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const opening = useRef(false);
  async function openDocument(id: string, onAccepted: () => void = () => {}) {
    if (opening.current || disabled) return;
    opening.current = true;
    setLoadError("");
    try {
      if (prepareDocument && id !== "sensitive") {
        setLoading(true);
        const result = await prepareDocument(id);
        if (result.document.id !== id) throw new Error("协议读取失败，请重试。");
        setPrepared(current => ({ ...current, [id]: result }));
      }
      readers.current[id]?.requestConsent(onAccepted);
    } catch (cause) { setLoadError(cause instanceof Error ? cause.message : "协议读取失败，请重试。"); }
    finally { opening.current = false; setLoading(false); }
  }
  const relevant = suppliedDocumentsForRole(role);
  const sequence = suppliedDocumentSequence(relevant, signed);
  useImperativeHandle(ref, () => ({ requestConsent(onAccepted) {
    const missing = relevant.find(document => !signed[document.id]);
    if (missing) void openDocument(missing.id, onAccepted);
  } }));
  // Display sensitive choices first without changing the signing sequence.
  const displayedDocuments = relevant.map((document, position) => ({ document, position }))
    .filter(({ document }) => (!sensitiveOnly || document.id === "sensitive"))
    .sort((a, b) => Number(b.document.id === "sensitive") - Number(a.document.id === "sensitive"));
  const displayedChoices = signed.sensitive && choices ? choices : selections;
  return <section aria-label="入驻协议文件" className={sensitiveOnly ? "rg-registration-consents" : "mb-6"}>
    <div className="rg-documents">{displayedDocuments.map(({ document: template, position }) => { const document = prepared[template.id]?.document ?? template; return <div key={document.id} className={hideSensitiveRow && document.id === "sensitive" ? "contents" : "grid gap-3"}>
        <ScrollConsent ref={reader => { readers.current[document.id] = reader; }} agreed={Boolean(signed[document.id])} onChange={() => {}} disabled={disabled} readOnly={Boolean(signed[document.id])}
          confirmBlockedReason={!sequence.isUnlocked(position) ? "请先阅读并签署上一份协议。" : document.id === "sensitive" && !signed[document.id] && !acceptedSuppliedChoices(sensitiveChoices) ? "请按顺序逐项确认敏感信息处理后再同意。" : ""}
          confirmFocusSelector={document.id === "sensitive" ? 'fieldset[data-consent-pending="true"]:not(:disabled) input[value="true"]' : undefined}
          onConfirm={signed[document.id] ? undefined : () => onSign(document.id, document.id === "sensitive" ? { ...sensitiveChoices } : undefined, prepared[document.id]?.renderedDigest)}
          title={document.title} hideTrigger confirmLabel={document.id === "sensitive" ? undefined : "阅读完成并签署"}>
          {document.id === "sensitive" ? document.blocks.map((block, index) => {
            const text = block.runs?.map(run => run.text).join("") ?? "";
            if (block.type !== "paragraph" || !text.startsWith("□ ")) {
              return <DocumentBody key={index} blocks={[block]} />;
            }
            const label = text.slice(2);
            const choiceIndex = document.blocks.slice(0, index + 1).filter(item => item.type === "paragraph" && item.runs?.map(run => run.text).join("").startsWith("□ ")).length - 1;
            return <SensitiveConsentChoice key={text} index={choiceIndex} text={label} choices={displayedChoices}
              onChange={selectChoice} disabled={disabled || Boolean(signed[document.id])} />;
          }) : <DocumentBody blocks={document.blocks} />}
        </ScrollConsent>
      {!(hideSensitiveRow && document.id === "sensitive") && <AgreementConfirmation compact={sensitiveOnly} showTitle={false} checked={Boolean(signed[document.id])} disabled={disabled || loading || Boolean(signed[document.id])} readDisabled={disabled || loading}
        onChange={() => void openDocument(document.id)} onOpen={() => void openDocument(document.id)} documentLabel={document.title}
        statement={sensitiveOnly ? "我已阅读并同意" : `${document.id === "sensitive" ? "本人确认上述选择均为本人真实、自愿、明确的意思表示" : "本人确认阅读并理解文件内容，签署为本人真实、自愿、明确的意思表示"}，并签署`} />}
    </div>; })}</div>
    {loadError && <p role="alert" className="rg-error">{loadError}</p>}
  </section>;
}
